<template>
  <div
    v-lazy-load="true"
    class="outfit-container"
    :class="getOutfitClass"
    @click="onClick"
    @keyup.enter="onClick"
    :tabindex="tabNavigationEnabled ? 0 : null"
  >
    <span v-if="isOutfitPromoted" class="badge-dot" />
    <span>
      <img
        class="outfit"
        v-if="visible"
        :src="getOutfitImageUrl"
        ref="outfit"
        @error="onError"
        @load="onLoadEnd"
        draggable="false"
      />
    </span>
  </div>
</template>

<script>
import { LazyLoad } from '@/directives/LazyLoadDirective';
import Blizzard from '@/services/Blizzard';
import UrlHelper from '@/services/UrlHelper';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import { BackgroundTypeEnum } from '@/enum/background';
import { OUTFIT_CATEGORY_KEY } from '@/constants';

export default {
  name: 'FashionOutfitPreview',
  props: [
    'outfitType', //: Number
    'outfitImageUrl', //: String
    'outfitId', //: Number
    'detailedOutfitOptions', //: Object
    'brandId', //: Number
    'brandBackground', //: String
    'tabNavigationEnabled', //:Boolean
  ],
  data: function() {
    return {
      visible: false, // Managed by LazyLoad
      observer: null, // Managed by LazyLoad
    };
  },
  methods: {
    setSelectedOutfit() {
      const initialTraits = Object.assign({}, this.$store.getters.traits);
      const outfitValue =
        this.resolvedOutfitType === OutfitTypeEnum.SPLIT
          ? this.detailedOutfitOptions
          : this.outfitId;

      this.$store.commit('setOutfit', {
        type: this.resolvedOutfitType,
        value: outfitValue,
      });

      // undo redo actions
      const initialBackground = {
        backgroundId: this.$store.getters.backgroundId,
        backgroundUrl: this.$store.getters.backgroundUrl,
        backgroundType: this.$store.getters.backgroundType,
      };

      if (
        this.currentBackgroundId !== this.brandId &&
        this.$store.getters.backgroundUrl !== this.brandBackground
      ) {
        const brandId = this.brandBackground === '' ? null : this.brandId;
        this.$store.commit('setBackgroundId', {
          id: brandId,
        });

        this.$store.commit('setBackgroundUrl', {
          url: this.brandBackground,
        });

        this.$store.commit('setBackgroundType', {
          type: BackgroundTypeEnum.OUTFIT,
        });
      }

      this.$store.commit('tuckControlSetForCurrentOutfit');

      this.$store.commit('updateAvatarActions', {
        initialTraits,
        initialBackground,
      });

      if (this.resolvedOutfitType === OutfitTypeEnum.SPLIT) {
        this.$store.commit('setColourPalettesForSelectedOutfitPieces');
      }

      this.isOutfitPromoted &&
        this.$store.commit('clearStaleOptionPromotion', {
          category: OUTFIT_CATEGORY_KEY,
          optionId: this.outfitId,
        });
    },
    onLoadEnd: function() {
      Blizzard.recordTraitPreviewLoad(false);
    },
    onError: function() {
      Blizzard.recordTraitPreviewLoad(true);

      if (this.$refs && this.$refs.outfit) {
        this.$refs.outfit.src = this.errorImage;
      }
    },
    onClick: function() {
      if (this.$refs && this.$refs.outfit && this.$refs.outfit.src === this.errorImage) {
        // retry loading all unloaded preview images in the grid when user clicks
        // on refresh icon
        this.$parent.$emit('reload-category-grid');
      } else {
        this.setSelectedOutfit();
      }
    },
  },
  computed: {
    resolvedOutfitType: function() {
      return !this.outfitType ? OutfitTypeEnum.ORIGINAL : this.outfitType;
    },
    getOutfitClass: function() {
      const isSelected =
        this.resolvedOutfitType === OutfitTypeEnum.SPLIT
          ? DetailedOutfitHelper.isSelected(this.$store.getters.traits, this.detailedOutfitOptions)
          : // Required check because a user may have a regular outfit & voltron outfit set in their traits
            this.$store.getters.traits.clothing_type !== OutfitTypeEnum.SPLIT &&
            this.$store.getters.traits.outfit === this.outfitId;

      return {
        selected: isSelected,
      };
    },
    errorImage: function() {
      return require('@/assets/reload_failed.png');
    },
    getOutfitImageUrl: function() {
      return UrlHelper.transformOutfitImageBaseURL(this.outfitImageUrl);
    },
    currentBackgroundId: function() {
      return this.$store.getters.backgroundId;
    },
    isOutfitPromoted: function() {
      return this.$store.getters.isOptionPromoted(OUTFIT_CATEGORY_KEY, this.outfitId);
    },
  },
  directives: {
    LazyLoad,
  },
};
</script>

<style scoped>
.outfit-container {
  height: 150px;
  /* Use relative so that child elements that are absolutely positioned render within this div */
  position: relative;
}

.outfit-container:active {
  transform: scale(0.9);
  transition-duration: 0.1s;
}

.outfit {
  height: 100%;
  max-width: 100%;
  margin: 0 auto;
}

.badge-dot {
  height: 8px;
  width: 8px;
  background-color: #0a92ff;
  border-radius: 50%;
  position: absolute;
  top: 4px;
  right: 4px;
  margin: 0;
}
</style>
